import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
import { crudFactoryV2, addCrudExtraReducersV2 } from "../helpers/CrudToolkitV2";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const {
  store,
  getById,
  update,
  remove,
  restore,
  fetchDeleted,
} = crudFactoryV2("employees", {
  withRestore: true,
  withDeleted: true,
});

/** GET /employees with optional Laravel-style query filters */
export const fetchEmployees = createAsyncThunk(
  "employees/fetchAll",
  async (arg, { rejectWithValue }) => {
    const params =
      arg && typeof arg === "object" && !Array.isArray(arg) ? arg : {};
    try {
      const res = await axios.get(`${BASE_URL}/employees`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
        params,
      });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch");
    }
  }
);

// ✅ Initial state
const initialState = {
  employees: [],
  deleted: [],
  record: null,
  isLoading: false,
  error: null,
  success: null,
  successDelete: null,
  errorDelete: null,
};

// ✅ Create slice
const EmployeesSlice = createSlice({
  name: "employees",
  initialState,
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
    clearRecord: (state) => {
      state.record = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;
        const page = action.meta.arg?.page || 1;
        const newRows = Array.isArray(payload?.data) ? payload.data : [];
        if (page === 1 || !Array.isArray(state.employees?.data)) {
          state.employees = payload;
          return;
        }
        const prevRows = state.employees.data;
        const ids = new Set(prevRows.map((r) => r?.id));
        state.employees = {
          ...payload,
          data: [
            ...prevRows,
            ...newRows.filter((r) => r?.id != null && !ids.has(r.id)),
          ],
          pagination: payload?.pagination ?? state.employees?.pagination,
        };
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    addCrudExtraReducersV2(builder, {
      store,
      getById,
      update,
      remove,
      restore,
      fetchDeleted,
      key: "employees",
      idKey: "user_id",
      success: "successDelete",
      error: "errorDelete",
    });
  },
});

// ✅ Export actions and thunks
export const { clearState, clearRecord } = EmployeesSlice.actions;

export {
  store as addEmployees,
  getById as getEmployeeById,
  update as updateEmployees,
  remove as deleteEmployees,
  restore as restoreEmployees,
  fetchDeleted as fetchDeletedEmployees,
};

export default EmployeesSlice.reducer;
